import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import {
  useCurrencyBalance,
  useTransactions,
  Transaction,
} from "@/hooks/use-currency";
import { useAuth } from "@/hooks/use-auth";
import {
  useCoinPackages,
  useInitiatePayment,
  useVerifyPayment,
  CoinPackage,
} from "@/hooks/use-paystack";
import { useLocation, useSearchParams } from "wouter";
import { useMobile } from "@/providers/mobile-provider";
import { Browser } from '@capacitor/browser'; // Import Capacitor Browser directly
import { Capacitor } from '@capacitor/core'; // Import Capacitor for platform detection
import {
  Coins,
  Info,
  Check,
  AlertTriangle,
  CreditCard,
  History,
  Gem,
  Loader2,
} from "lucide-react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { PendingTransactions } from "@/components/currency/PendingTransactions";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 12,
    },
  },
};

const FormSchema = z.object({
  packageId: z.number({
    required_error: "Please select a package",
  }),
  email: z.string().email({
    message: "Please enter a valid email address",
  }),
});

export function TopUp() {
  const { toast } = useToast();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [_, setLocation] = useLocation();
  const { isNative } = useMobile(); // We'll use our own browser functions
  const isNativePlatform = Capacitor.isNativePlatform(); // Direct check for native platform
  const [transactionLimit, setTransactionLimit] = useState<number>(30);
  const { data: coinPackages, isLoading: isLoadingPackages, error: packagesError } =
    useCoinPackages();
  const initiatePayment = useInitiatePayment();
  const verifyPayment = useVerifyPayment();
  const { data: balance, error: balanceError } = useCurrencyBalance();
  const { data: transactions } = useTransactions(transactionLimit);
  const [activeTab, setActiveTab] = useState<"buy" | "topups" | "transactions">("buy");
  const [verificationStatus, setVerificationStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [verificationError, setVerificationError] = useState<string | null>(null);
  const [browserOpened, setBrowserOpened] = useState<boolean>(false);
  const [currentReference, setCurrentReference] = useState<string>("");

  // Add state for confirmation dialog
  const [selectedPackage, setSelectedPackage] = useState<CoinPackage | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      email: user?.email ?? "",
    },
  });
  
  // Extract platform information once for reuse
  const currentPlatform = isNativePlatform ? Capacitor.getPlatform() : 'web';

  // Handle payment verification from URL parameter with retry logic
  useEffect(() => {
    const reference = searchParams.get("reference");
    if (reference && verificationStatus === "idle") {
      const verifyWithRetry = async (retries = 3) => {
        try {
          await verifyTransaction(reference);
        } catch (error) {
          if (retries > 0) {
            // Wait 2 seconds before retrying
            setTimeout(() => verifyWithRetry(retries - 1), 2000);
          } else {
            setVerificationStatus("error");
            setVerificationError("Maximum retries exceeded. Please try again or contact support.");
            toast({
              title: "Verification Error",
              description: "Failed to verify payment after multiple attempts",
              variant: "destructive",
            });
          }
        }
      };
      verifyWithRetry();
    }
  }, [searchParams]);

  // Listen for app resuming from in-app browser to check for payment status
  useEffect(() => {
    // If browser was opened and we're now back in the app
    if (browserOpened) {
      // Reset the browser state
      setBrowserOpened(false);
      
      // Use a timeout to allow the app to fully resume
      setTimeout(() => {
        console.log("App resumed after in-app browser, checking payment status...");
        // Go to the transactions tab to show history
        setActiveTab("topups");
        
        // Check for pending transactions that need to be verified
        // This will happen automatically via the pending transactions component
      }, 1000);
    }
  }, [browserOpened]);

  const verifyTransaction = async (reference: string) => {
    try {
      setVerificationStatus("loading");
      setVerificationError(null);

      const result = await verifyPayment.mutateAsync(reference);

      if (result.success) {
        setVerificationStatus("success");
        toast({
          title: "Payment Successful",
          description: `Your coins have been added to your account.`,
          variant: "default",
        });
      } else {
        setVerificationStatus("error");
        setVerificationError(result.message || "Failed to verify payment");
        toast({
          title: "Payment Failed",
          description: result.message || "Failed to verify payment",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Payment verification error:", error);
      setVerificationStatus("error");
      setVerificationError(error instanceof Error ? error.message : "Failed to verify payment");
      toast({
        title: "Verification Error",
        description: "There was an error verifying your payment",
        variant: "destructive",
      });
      throw error; // Re-throw for retry logic
    }
  };
  
  // Add helper function for opening browser with proper URL change detection
  const openInAppBrowser = async (url: string, reference: string) => {
    console.log('Opening in-app browser with URL:', url);
    try {
      if (isNativePlatform) {
        // Set the browser as opened
        setBrowserOpened(true);
        setCurrentReference(reference);
        
        // Add listener before opening browser to detect URL changes
        Browser.addListener('browserPageLoaded', async (info) => {
          console.log('Browser page loaded:', info.url);
          
          // Check if the URL contains our payment callback pattern
          if (info.url.includes('payment/callback') || 
              info.url.includes('checkout/callback') || 
              info.url.includes('checkout/receipt') ||
              info.url.includes('checkout/success') ||
              (info.url.includes('status=success') && info.url.includes(reference))) {
            
            console.log('Payment successful URL detected, closing browser');
            
            // Small delay to allow the success page to be visible briefly
            setTimeout(async () => {
              try {
                // Close the browser
                await Browser.close();
                console.log('Browser closed after successful payment');
                
                // Verify the payment
                if (reference) {
                  console.log('Auto-verifying payment with reference:', reference);
                  setVerificationStatus('loading');
                  try {
                    await verifyTransaction(reference);
                  } catch (err) {
                    console.error('Error auto-verifying payment:', err);
                  }
                }
              } catch (error) {
                console.error('Error closing browser:', error);
              }
            }, 1500);
          }
        });
        
        // Use Capacitor Browser on native platforms
        await Browser.open({ url, presentationStyle: 'popover' });
      } else {
        // Use regular window.open on web
        window.open(url, '_blank');
      }
    } catch (error) {
      console.error('Error opening browser:', error);
      // Fallback to window.open
      window.open(url, '_blank');
    }
  };

  // Show error state if any critical data failed to load
  if (packagesError || balanceError) {
    return (
      <div className="space-y-6">
        <Alert className="bg-destructive/10 border-destructive/30">
          <AlertTriangle className="h-4 w-4 text-destructive" />
          <AlertTitle>Error Loading Data</AlertTitle>
          <AlertDescription>
            {packagesError?.message ?? balanceError?.message ?? "Failed to load necessary data. Please try again."}
          </AlertDescription>
        </Alert>
        <Button 
          onClick={() => window.location.reload()}
          className="w-full"
        >
          Retry
        </Button>
      </div>
    );
  }

  const onSubmit = async (data: z.infer<typeof FormSchema>) => {
    try {
      const platform = isNativePlatform ? Capacitor.getPlatform() : 'web';
      
      const result = await initiatePayment.mutateAsync({
        packageId: data.packageId,
        email: data.email,
        platform // Pass platform information to the API
      });

      if (result.success && result.paymentUrl) {
        if (isNativePlatform) {
          console.log("Opening Paystack payment in in-app browser");
          // Set the browser as opened
          setBrowserOpened(true);
          
          // Use our direct browser opening function
          await openInAppBrowser(result.paymentUrl, result.reference);
          
          // The return to app logic will handle verification
          // when the user completes payment or closes the browser
          
        } else {
          // Standard web approach - redirect
          window.location.href = result.paymentUrl;
        }
      } else {
        toast({
          title: "Payment Error",
          description: result.message ?? "Failed to initiate payment",
          variant: "destructive",
        });
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Failed to initiate payment";
      console.error("Payment initiation error:", error);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      // Re-throw the error to be handled by the form's error boundary
      throw error;
    }
  };

  // Function to render coin package cards
  const renderPackageCard = (pkg: CoinPackage) => {
    const isPremium = pkg.coinType === "premium";

    return (
      <motion.div
        key={pkg.id}
        variants={itemVariants}
        className="col-span-1"
      >
        <Card className="relative overflow-hidden border-gray-800 bg-black/40">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>{pkg.name}</span>
              {isPremium && (
                <Badge className="bg-purple-500/20 text-purple-300 border-purple-700">
                  Premium
                </Badge>
              )}
            </CardTitle>
            <CardDescription>{pkg.description}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold flex items-center gap-2">
              <span>₵{(pkg.amount).toFixed(2)}</span>
            </div>
            <div className="mt-2 flex items-center gap-1 text-sm">
              <span className="text-gray-400">You get:</span>
              <span className="font-medium">{pkg.coins}</span>
              {isPremium ? (
                <Gem className="h-4 w-4 text-purple-400" />
              ) : (
                <Coins className="h-4 w-4 text-amber-400" />
              )}
            </div>
          </CardContent>
          <CardFooter>
            <Button 
              className="w-full" 
              onClick={() => {
                setSelectedPackage(pkg);
                setConfirmDialogOpen(true);
              }}
            >
              Buy
            </Button>
          </CardFooter>
        </Card>
      </motion.div>
    );
  };

  // Add confirmation dialog
  const handleConfirmPurchase = () => {
    if (!selectedPackage) return;
    form.setValue("packageId", selectedPackage.id);
    form.handleSubmit(onSubmit)();
    setConfirmDialogOpen(false);
  };

  // Function to render transaction history items
  const renderTransactionItem = (transaction: Transaction) => {
    if (transaction.transactionType !== "purchase") return null;

    const isPremium = transaction.currencyType === "premium";

    return (
      <motion.div
        key={transaction.id}
        variants={itemVariants}
        className="flex items-center justify-between p-3 rounded-lg border border-gray-800 bg-black/40 mb-2"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-black/20 flex items-center justify-center">
            {isPremium ? (
              <Gem className="h-5 w-5 text-purple-400" />
            ) : (
              <Coins className="h-5 w-5 text-yellow-400" />
            )}
          </div>
          <div>
            <div className="font-medium">{transaction.description}</div>
            <div className="text-sm text-gray-400">
              {format(new Date(transaction.createdAt), "MMM d, yyyy • h:mm a")}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={isPremium ? "text-purple-400" : "text-yellow-400"}>
            +{transaction.amount}
          </span>
          <span className="text-gray-400">{isPremium ? "gems" : "coins"}</span>
        </div>
      </motion.div>
    );
  };

  // Filter just the purchase transactions
  const purchaseTransactions =
    transactions?.filter(
      (t: Transaction) => t.transactionType === "purchase" && t.amount > 0,
    ) ?? [];

  // Show loading state
  if (isLoadingPackages) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Top Up Your Account
          </h2>
          <div className="flex items-center gap-2">
            <Skeleton className="h-6 w-16 bg-gray-800" />
            <Skeleton className="h-6 w-10 bg-gray-800" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="border-gray-800">
              <CardHeader>
                <Skeleton className="h-6 w-24 bg-gray-800 mb-2" />
                <Skeleton className="h-4 w-full bg-gray-800" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-12 w-24 mx-auto bg-gray-800" />
              </CardContent>
              <CardFooter>
                <Skeleton className="h-4 w-16 bg-gray-800" />
                <div className="ml-auto">
                  <Skeleton className="h-8 w-16 bg-gray-800" />
                </div>
              </CardFooter>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // Show verification status
  if (verificationStatus === "loading") {
    return (
      <div className="space-y-6">
        <Alert className="bg-primary/10 border-primary/30">
          <Info className="h-4 w-4" />
          <AlertTitle>Verifying your payment</AlertTitle>
          <AlertDescription>
            Please wait while we verify your payment...
          </AlertDescription>
        </Alert>

        <div className="flex items-center justify-center py-12">
          <div className="animate-pulse text-primary text-center">
            <CreditCard className="h-16 w-16 mx-auto mb-4" />
            <p className="text-lg font-medium">Processing your transaction</p>
            <p className="text-sm text-gray-400 mt-2">
              This may take a few moments...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show success message
  if (verificationStatus === "success") {
    const reference = searchParams.get("reference") ?? "";

    return (
      <div className="space-y-6">
        <Alert className="bg-green-500/10 border-green-500/30">
          <Check className="h-4 w-4 text-green-500" />
          <AlertTitle>Payment Successful!</AlertTitle>
          <AlertDescription>
            Your purchase has been completed successfully. Your currency has been added to your account. Thank you for your purchase!
          </AlertDescription>
        </Alert>

        <div className="p-8 border border-gray-800 rounded-lg bg-black/40 text-center">
          <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center text-green-500 mx-auto mb-4">
            <Check className="h-8 w-8" />
          </div>
          <h3 className="text-xl font-bold mb-2">Transaction Complete</h3>
          <p className="text-gray-400 mb-4">Reference: {reference}</p>

          <div className="flex justify-center gap-4 mt-6">
            <Button
              onClick={() => {
                setVerificationStatus("idle");
                setActiveTab("buy");
              }}
            >
              Make Another Purchase
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setVerificationStatus("idle");
                setActiveTab("topups");
              }}
            >
              View Top Up History
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Show error message
  if (verificationStatus === "error") {
    return (
      <div className="space-y-6">
        <Alert className="bg-destructive/10 border-destructive/30">
          <AlertTriangle className="h-4 w-4 text-destructive" />
          <AlertTitle>Payment Verification Failed</AlertTitle>
          <AlertDescription>
            {verificationError ?? "We couldn't verify your payment. If you've already been charged, please contact support."}
          </AlertDescription>
        </Alert>

        <div className="p-8 border border-gray-800 rounded-lg bg-black/40 text-center">
          <div className="w-16 h-16 rounded-full bg-destructive/20 flex items-center justify-center text-destructive mx-auto mb-4">
            <AlertTriangle className="h-8 w-8" />
          </div>
          <h3 className="text-xl font-bold mb-2">Transaction Error</h3>
          <p className="text-gray-400 mb-4">
            There was a problem processing your payment.
          </p>

          <div className="flex justify-center gap-4 mt-6">
            <Button
              onClick={() => {
                setVerificationStatus("idle");
                setActiveTab("buy");
              }}
            >
              Try Again
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setVerificationStatus("idle");
                setActiveTab("topups");
              }}
            >
              View Top Up History
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Main TopUp UI
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <CreditCard className="h-5 w-5" />
          Wallet
        </h2>
        <div className="flex items-center gap-4">
          <span className="text-gray-400">Balance:</span>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 font-semibold">
              <Coins className="h-4 w-4 text-yellow-400" />
              <span className="text-yellow-400">{balance?.currency ?? 0}</span>
            </div>
            <div className="flex items-center gap-1 font-semibold">
              <Coins className="h-4 w-4 text-purple-400" />
              <span className="text-purple-400">
                {balance?.premiumCurrency ?? 0}
              </span>
            </div>
          </div>
        </div>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(value) =>
          setActiveTab(value as "buy" | "topups" | "transactions")
        }
      >
        <TabsList className="grid w-full grid-cols-3 mb-6">
          <TabsTrigger value="buy" className="flex items-center gap-2">
            <CreditCard className="h-4 w-4" />
            Buy Coins
          </TabsTrigger>
          <TabsTrigger value="topups" className="flex items-center gap-2">
            <Coins className="h-4 w-4" />
            Top Ups
          </TabsTrigger>
          <TabsTrigger value="transactions" className="flex items-center gap-2">
            <History className="h-4 w-4" />
            Transactions
          </TabsTrigger>
        </TabsList>

        <TabsContent value="buy">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <div className="space-y-3">
                {/* Coin packages selection - List style */}
                <FormField
                  control={form.control}
                  name="packageId"
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <div className="flex items-center gap-1.5 mb-2">
                        <Coins className="h-4 w-4 text-primary" />
                        <FormLabel className="text-sm font-medium">
                          Choose a Coin Package <span className="text-gray-500 text-xs">(Payments are non-refundable)</span>
                        </FormLabel>
                      </div>
                      
                      {/* Compact list view packages */}
                      <div className="space-y-2 max-w-md mx-auto">
                        {coinPackages?.map((pkg) => (
                          <div 
                            key={pkg.id}
                            className={`border rounded-lg overflow-hidden cursor-pointer transition-colors duration-200 ${
                              form.watch("packageId") === pkg.id 
                                ? "border-green-600 bg-black" 
                                : "border-gray-800 bg-black/60 hover:border-gray-600"
                            }`}
                            onClick={() => form.setValue("packageId", pkg.id)}
                          >
                            <div className="p-3">
                              <div className="flex flex-col md:flex-row md:items-center justify-between">
                                <div className="space-y-0.5 mb-2 md:mb-0">
                                  <h3 className="text-lg font-semibold text-white">{pkg.name}</h3>
                                  <p className="text-xs text-gray-400">{pkg.description}</p>
                                </div>
                                
                                <div className="flex flex-row md:flex-col items-center md:items-end justify-between">
                                  <div className="text-xl font-bold text-white">₵{pkg.amount.toFixed(2)}</div>
                                  
                                  <div className="flex items-center gap-1.5 text-sm">
                                    <span className="text-gray-300">You get:</span>
                                    <span className="font-medium text-yellow-400">{pkg.coins}</span>
                                    {pkg.coinType === "premium" ? (
                                      <Gem className="h-4 w-4 text-purple-400" />
                                    ) : (
                                      <Coins className="h-4 w-4 text-yellow-400" />
                                    )}
                                  </div>
                                </div>
                              </div>
                              
                              <div className="mt-2 pt-2 border-t border-gray-800 flex items-center justify-between">
                                <div className="flex items-center">
                                  {form.watch("packageId") === pkg.id && (
                                    <div className="bg-green-600 rounded-full p-0.5 mr-2">
                                      <Check className="h-3 w-3 text-white" />
                                    </div>
                                  )}
                                  <span className={`text-xs ${form.watch("packageId") === pkg.id ? "text-green-500" : "text-gray-400"}`}>
                                    {form.watch("packageId") === pkg.id ? "Selected" : "Click to select"}
                                  </span>
                                </div>
                                <Button
                                  type="button"
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700 h-8 px-4"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    form.setValue("packageId", pkg.id);
                                    // Auto scroll to the bottom to see the submit button
                                    setTimeout(() => {
                                      document.getElementById("payment-button")?.scrollIntoView({ behavior: "smooth", block: "center" });
                                    }, 100);
                                  }}
                                >
                                  Buy
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Email field in a more compact form */}
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem className="space-y-1 max-w-md mx-auto">
                      <div className="flex justify-between items-center">
                        <FormLabel className="text-sm">Email for Receipt</FormLabel>
                        <FormDescription className="text-xs mt-0">
                          {user?.email ? "Email is auto-filled from your account" : "We'll send payment confirmation here"}
                        </FormDescription>
                      </div>
                      <FormControl>
                        <div className="relative">
                          <Input
                            placeholder="Enter your email address"
                            {...field}
                            className={`bg-black/40 border-gray-800 focus:border-primary h-9 ${
                              user?.email ? "text-gray-400" : ""
                            }`}
                            disabled={!!user?.email}
                            readOnly={!!user?.email}
                          />
                          {user?.email && (
                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                              <Badge variant="outline" className="border-gray-700 bg-gray-800/50 text-xs px-1.5">
                                Verified
                              </Badge>
                            </div>
                          )}
                        </div>
                      </FormControl>
                      <FormMessage />
                      {!user?.email && (
                        <p className="text-xs text-gray-500">Add an email to receive payment receipts</p>
                      )}
                    </FormItem>
                  )}
                />
              </div>

              {/* Payment info alert */}
              <Alert className="bg-primary/10 border-primary/30 py-2 mt-3 mb-4 max-w-md mx-auto">
                <div className="flex items-start gap-2">
                  <Info className="h-4 w-4 mt-0.5" />
                  <div className="space-y-0.5">
                    <AlertDescription className="text-xs text-gray-300">
                      Payments are securely processed by Paystack. {isNativePlatform 
                        ? "You'll be redirected to a secure payment page within the app." 
                        : "You will be redirected to complete your payment."}
                    </AlertDescription>
                  </div>
                </div>
              </Alert>

              <Button
                id="payment-button"
                type="submit"
                className="w-full max-w-md mx-auto block"
                disabled={!form.watch("packageId") || initiatePayment.isPending}
              >
                {initiatePayment.isPending ? (
                  <>Processing...</>
                ) : (
                  <>Proceed to Payment</>
                )}
              </Button>
            </form>
          </Form>
        </TabsContent>

        <TabsContent value="topups">
          <div className="space-y-6">
            <Separator className="my-6" />

            <div className="flex items-center gap-2 mb-4">
              <Coins className="h-5 w-5 text-primary" />
              <h3 className="text-base font-medium">Your Top Up History</h3>
            </div>

            {purchaseTransactions.length > 0 ? (
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="space-y-2"
              >
                {purchaseTransactions.map((transaction: Transaction) =>
                  renderTransactionItem(transaction),
                )}
              </motion.div>
            ) : (
              <div className="text-center py-8 border border-dashed border-gray-800 rounded-lg">
                <Coins className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-300">
                  No top up history yet
                </h3>
                <p className="text-gray-500 mt-2">
                  Your coin purchases will appear here
                </p>
              </div>
            )}
            <div className="flex justify-center mt-4">
                <Button
                  variant="outline"
                  onClick={() => setTransactionLimit((prev) => prev + 50)}
                >
                  Show More
                </Button>
              </div>
            
          </div>
        </TabsContent>

        <TabsContent value="transactions">
          <div className="space-y-6">
            <PendingTransactions />
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent className="bg-gray-900 border border-gray-700">
          <DialogHeader>
            <DialogTitle>Confirm Purchase</DialogTitle>
            <DialogDescription>
              Please confirm your coin package purchase
            </DialogDescription>
          </DialogHeader>
          {selectedPackage && (
            <div className="py-4">
              <div className="flex items-center justify-between mb-4">
                <span className="font-medium text-white">
                  {selectedPackage.name}
                </span>
                <div className="flex items-center gap-1">
                  <span className="text-white">₵{selectedPackage.amount.toFixed(2)}</span>
                </div>
              </div>
              <p className="text-sm text-gray-300 mb-4">
                {selectedPackage.description}
              </p>
              <div className="text-sm text-gray-400 bg-gray-800 p-3 rounded-md">
                You will receive:
                <div className="flex items-center gap-1 mt-2 text-white">
                  <span>{selectedPackage.coins}</span>
                  {selectedPackage.coinType === "premium" ? (
                    <Gem className="h-4 w-4 text-purple-400" />
                  ) : (
                    <Coins className="h-4 w-4 text-amber-400" />
                  )}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmDialogOpen(false)}
              disabled={initiatePayment.isPending}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmPurchase}
              disabled={initiatePayment.isPending}
            >
              {initiatePayment.isPending ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Processing...
                </div>
              ) : (
                "Confirm Purchase"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
